import React, { useEffect, useState } from "react";
import "./Antibiogram.scss";
import { steps } from "./config";
import { Answers, Config, Diagnosis, Drug, StepId } from "./types";
import { CategoryStep } from "./components/steps/CategoryStep";
import { ProtocolStep } from "./components/steps/ProtocolStep";
import { DiagnosisStep } from "./components/steps/DiagnosisStep";
import { SensitivitiesStep, SensitivitiesAnswers } from "./components/SensitivitiesStep";
import { buildActivePath, resolveNextStep } from "./utils";
import { Summary } from "./components/Summary";
import ConfigService from "../../services/ConfigService";

const APP_NAME = "ANTIBIOGRAM";
const CONFIG_NAME = "antibiogram-admin";

export default function Antibiogram({ hospital }: { hospital: string }) {
	const [config, setConfig] = useState<Config | null>(null);
	const [categories, setCategories] = useState<{ id: string; name: string; imagePath?: string }[]>(
		[],
	);
	const [drugs, setDrugs] = useState<Drug[]>([]);

	const [currentStepId, setCurrentStepId] = useState<StepId>("category");
	const [answers, setAnswers] = useState<Answers>({});
	// isDone is true once the user has completed the sensitivities step
	const [isDone, setIsDone] = useState(false);

	const onSelect = (value: any) => {
		if (!currentStep) return;
		const stepKey = currentStep.id;
		const newAnswers = {
			...answers,
			[stepKey]: value,
		};
		setAnswers(newAnswers);
		const nextStepId = resolveNextStep(currentStep, value);
		if (nextStepId) {
			setCurrentStepId(nextStepId);
		}
	};

	const onSensitivitiesDone = (sens: SensitivitiesAnswers) => {
		setAnswers((prev) => ({ ...prev, sensitivities: sens }));
		setIsDone(true);
	};

	const currentStep = steps.find((s) => s.id === currentStepId);

	const path = buildActivePath("category", answers, steps);

	// Progress bar segments - fixed 5 stages
	const PROGRESS_SEGMENTS: { key: string; label: string; stepIds: string[] }[] = [
		{ key: "category", label: "קטגוריה", stepIds: ["category"] },
		{ key: "protocol", label: "פרוטוקול", stepIds: ["protocol"] },
		{ key: "diagnosis", label: "אבחנה", stepIds: ["diagnosis"] },
		{ key: "sensitivities", label: "רגישויות", stepIds: ["sensitivities"] },
		{ key: "recommendations", label: "המלצות", stepIds: [] },
	];

	const getSegmentState = (segmentIdx: number): "completed" | "active" | "pending" => {
		if (isDone) {
			return segmentIdx < 4 ? "completed" : "active";
		}
		const segment = PROGRESS_SEGMENTS[segmentIdx];
		if (segment.stepIds.includes(currentStepId)) return "active";
		const laterSegment = PROGRESS_SEGMENTS.slice(segmentIdx + 1).some((s) =>
			s.stepIds.includes(currentStepId),
		);
		if (laterSegment) return "completed";
		return "pending";
	};

	const handleSegmentClick = (segmentIdx: number) => {
		const state = getSegmentState(segmentIdx);
		if (state !== "completed") return;
		const segment = PROGRESS_SEGMENTS[segmentIdx];
		const firstStepId = segment.stepIds.find((id) => path.includes(id));
		if (firstStepId) {
			setCurrentStepId(firstStepId as StepId);
			// Clear answers for all steps including and after the clicked one
			const displayPath = path;
			const idx = displayPath.indexOf(firstStepId);
			const stepsToClear = displayPath.slice(idx);
			const newAnswers = { ...answers };
			stepsToClear.forEach((id) => delete newAnswers[id]);
			setAnswers(newAnswers);
			setIsDone(false);
		}
	};

	const handleBack = () => {
		if (isDone) {
			// Go back to sensitivities step
			setIsDone(false);
			setCurrentStepId("sensitivities");
			const newAnswers = { ...answers };
			delete newAnswers.sensitivities;
			setAnswers(newAnswers);
			return;
		}
		const previousStepId = path[path.length - 2];
		if (previousStepId) {
			setCurrentStepId(previousStepId as StepId);
			const newAnswers = { ...answers };
			delete newAnswers[previousStepId];
			setAnswers(newAnswers);
		}
	};

	const handleReset = () => {
		setAnswers({});
		setCurrentStepId("category");
		setIsDone(false);
	};

	// Extract unique drug IDs from a diagnosis (from both firstLine and alternative treatment options)
	const getDrugIdsFromDiagnosis = (diagnosis: Diagnosis | undefined): string[] => {
		if (!diagnosis?.treatment) return [];
		const drugIds = new Set<string>();

		const extractFromOptions = (options: { drugs: { drugId: string }[] }[] | undefined) => {
			options?.forEach((option) => {
				option.drugs?.forEach((drug) => {
					if (drug.drugId) drugIds.add(drug.drugId);
				});
			});
		};

		extractFromOptions(diagnosis.treatment.firstLine?.options);
		extractFromOptions(diagnosis.treatment.alternative?.options);

		return Array.from(drugIds);
	};

	// Get the selected diagnosis based on current answers
	const getSelectedDiagnosis = (): Diagnosis | undefined => {
		if (!config || !answers.protocol || !answers.diagnosis) return undefined;
		const protocol = config.protocols.find((p) => String(p.id) === String(answers.protocol));
		return protocol?.diagnoses.find((d) => String(d.id) === String(answers.diagnosis));
	};

	useEffect(() => {
		ConfigService.getConfig(APP_NAME, CONFIG_NAME, hospital)
			.then((res) => {
				setConfig(res.data.antibiohelpConfig);
				setDrugs(res.data.drugs ?? []);
			})
			.catch((err) => {
				console.error("error fetching antibiogram data", err);
			});
		ConfigService.getConfig(APP_NAME, "categories", hospital)
			.then((res) => {
				setCategories(res.data.categories ?? []);
			})
			.catch((err) => {
				console.error("error fetching categories data", err);
			});
	}, [hospital]);

	const progressBar = (
		<div className="progress-bar-container">
			{PROGRESS_SEGMENTS.map((segment, idx) => {
				const state = getSegmentState(idx);
				const prevState = idx > 0 ? getSegmentState(idx - 1) : null;
				return (
					<React.Fragment key={segment.key}>
						{idx > 0 && (
							<div className={`progress-connector ${prevState === "completed" ? "filled" : "pending"}`} />
						)}
						<button
							type="button"
							className={`progress-segment ${state}`}
							onClick={() => handleSegmentClick(idx)}
							disabled={state !== "completed"}
						>
							<div className="progress-circle">{idx + 1}</div>
							<div className="progress-label">{segment.label}</div>
						</button>
					</React.Fragment>
				);
			})}
		</div>
	);

	if (isDone && config) {
		return (
			<Summary
				config={config}
				categories={categories}
				drugs={drugs}
				steps={steps}
				answers={answers}
				onBack={handleBack}
				handleReset={handleReset}
				progressBar={progressBar}
			/>
		);
	}

	const renderStep = () => {
		if (!currentStep) return <div>Step not found</div>;

		switch (currentStep.id) {
			case "category":
				return (
					<CategoryStep
						categories={categories.map((cat) => ({
							id: cat.id,
							label: cat.name,
							imagePath: cat.imagePath,
						}))}
						onSelect={onSelect}
						selectedCategory={answers.category}
					/>
				);
			case "protocol":
				return (
					<ProtocolStep
						protocols={config?.protocols ?? []}
						onSelect={onSelect}
						answers={answers}
					/>
				);
			case "diagnosis":
				return <DiagnosisStep config={config!} onSelect={onSelect} answers={answers} />;
			case "sensitivities":
				return (
					<SensitivitiesStep
						drugs={drugs}
						relevantDrugIds={getDrugIdsFromDiagnosis(getSelectedDiagnosis())}
						onDone={onSensitivitiesDone}
					/>
				);
			default:
				return <div>Step not found</div>;
		}
	};

	return (
		<div className="antibiogram-container">
			<div className="antibiogram-header">
				<h2>מתן אנטיביוטיקה במלר"ד</h2>
			</div>

			{progressBar}

			<div className="antibiogram-content">
				<div className="step-content">
					<div className="step-header">
						<button className="back-step-button" onClick={handleBack}>
							חזור →
						</button>
						<h3 className="step-title">{currentStep?.title || ""}</h3>
					</div>
					{renderStep()}
				</div>
			</div>
		</div>
	);
}
